/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CSSObject } from '@emotion/react'
import { ClassNames } from '@emotion/react'
import classNames from 'classnames'
import type { FC } from 'react'
import React from 'react'

import type {
  FormFieldProps,
  HelperTextProps,
  ValidationTextProps
} from './types'
import type { Theme } from '../../theme-provider/types'
import { EMPTY_OBJECT } from '../../utils/object'
import Grid from '../_layouts/grid/grid'
import Box from '../_primitives/box/box'
import Flex from '../_primitives/flex'
import FlexInLine from '../_primitives/flex/flex-inline'
import Icon from '../_primitives/icon/icon'
import Label from '../_primitives/label/label'
import Text from '../_primitives/text/text'

const classes = {
  labelMainWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    bottom: '100%',
    width: '100%'
  } as CSSObject,
  labelWithIndicator: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    bottom: '100%'
  } as CSSObject,
  labelWrapper: {
    position: 'absolute',
    bottom: '100%'
  },
  labelOverFlow: {
    overflow: 'hidden'
  } as CSSObject,
  validationText: {
    position: 'absolute',
    top: '100%',
    wordBreak: 'break-all',
    width: '100%',
    '&.no-wrap': {
      '.csg-tui-icon': {
        alignSelf: 'flex-start',
        transform: 'translateY(0.25em)'
      }
    }
  } as CSSObject,
  helperText: {
    position: 'absolute',
    top: '100%',
    wordBreak: 'break-all',
    width: '100%'
  } as CSSObject,
  labelRightAddon: {
    zIndex: 1
  } as CSSObject
}

const FormField: FC<FormFieldProps> = React.forwardRef<
  HTMLDivElement,
  FormFieldProps & { theme?: Theme }
>(function FormField(props, ref) {
  const {
    children,
    label,
    labelIndicator,
    helperText,
    helperTextProps = EMPTY_OBJECT as HelperTextProps,
    validationText,
    validationTextProps = EMPTY_OBJECT as ValidationTextProps,
    showErrorIcon = true,
    labelRightAddon,
    className,
    lineHeight = 'body',
    gap = 'xs',
    labelProps,
    loading,
    labelSkeleton,
    ...rest
  } = props
  const labelContent = loading ? (
    labelSkeleton
  ) : (
    <Text className={'csg-tui-label-text'} ellipsis>
      {label}
    </Text>
  )
  return (
    <ClassNames>
      {({ css, cx }) => (
        <Grid
          ref={ref}
          relative
          className={classNames('csg-tui-form-field-grid', className)}
          gap={gap}
          alignItems="center"
          {...rest}
        >
          {labelIndicator || labelRightAddon ? (
            <FlexInLine
              className={cx({
                'csg-tui-label-main-wrapper': true,
                [css(classes.labelMainWrapper)]: true
              })}
              gap="xs-3"
              justifyContent={labelRightAddon ? 'space-between' : undefined}
            >
              <FlexInLine
                gap="xs-3"
                className={cx({
                  'csg-tui-label-wrapper': true,
                  [css(classes.labelOverFlow)]: true
                })}
              >
                <Label
                  className={cx({
                    'csg-tui-label': true,
                    [css(classes.labelOverFlow)]: true
                  })}
                  ellipsis={false}
                  lineHeight={lineHeight}
                  {...labelProps}
                >
                  {labelContent}
                </Label>
                {labelIndicator && (
                  <FlexInLine as="span">{labelIndicator}</FlexInLine>
                )}
              </FlexInLine>
              {labelRightAddon && (
                <Box
                  as="span"
                  className={cx({
                    'csg-tui-label-right-addon': true,
                    [css(classes.labelRightAddon)]: true
                  })}
                  pl="xs"
                >
                  {labelRightAddon}
                </Box>
              )}
            </FlexInLine>
          ) : (
            <Label
              className={cx({
                'csg-tui-label': true,
                [css(classes.labelMainWrapper)]: true
              })}
              ellipsis={false}
              lineHeight={lineHeight}
              {...labelProps}
            >
              {labelContent}
            </Label>
          )}
          {children}
          {helperText || validationText ? (
            <Text
              className={cx({
                [`csg-tui-${
                  helperText ? 'helper-text' : 'validation-text'
                }-wrapper`]: true,
                [css(classes[helperText ? 'helperText' : 'validationText'])]:
                  true,
                'no-wrap': helperText
                  ? helperTextProps.noWrap
                  : validationTextProps.noWrap
              })}
              fontSize="xxs"
            >
              {/* mt="0.125em" is required due to position absolute */}
              {validationText ? (
                <Flex as="span" alignItems="center" gap="xs-2" mt="0.125em">
                  {showErrorIcon && (
                    <Icon icon="info" color="danger" size="1em" />
                  )}
                  <Text
                    color="danger"
                    ellipsis={!validationTextProps.noWrap}
                    className={cx({
                      'csg-tui-validation-text': true
                    })}
                    alignItems="center"
                  >
                    {validationText}
                  </Text>
                </Flex>
              ) : (
                <Text
                  color="textSecondary"
                  ellipsis={!helperTextProps.noWrap}
                  className={cx({
                    'csg-tui-helper-text': true
                  })}
                  mt="0.125em"
                >
                  {helperText}
                </Text>
              )}
            </Text>
          ) : null}
        </Grid>
      )}
    </ClassNames>
  )
})

export default FormField
